import React, { useEffect, useState } from 'react';
import { useCredits } from '../../contexts/CreditContext';

export const TransactionHistory: React.FC = () => {
  const { transactions, transactionsMeta, transactionsLoading, fetchTransactions } = useCredits();
  
  // Filter states
  const [transactionType, setTransactionType] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  
  // Pagination state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const transactionTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'credit', label: 'Credits Purchased' },
    { value: 'debit', label: 'Credits Consumed' },
    { value: 'credit_addition', label: 'Bonus Credits' },
  ];

  useEffect(() => {
    // Initial load
    fetchTransactions({
      limit: 20,
      offset: 0,
      transactionType: transactionType === 'all' ? undefined : transactionType,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    setCurrentOffset(0);
  }, [fetchTransactions, transactionType, dateFrom, dateTo]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const newOffset = currentOffset + 20;
    
    await fetchTransactions({
      limit: 20,
      offset: newOffset,
      transactionType: transactionType === 'all' ? undefined : transactionType,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    
    setCurrentOffset(newOffset);
    setLoadingMore(false);
  };

  const clearFilters = () => {
    setTransactionType('all');
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return '💳';
      case 'debit':
        return '⚡';
      case 'credit_addition':
        return '🎁';
      default:
        return '💎';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'credit':
        return 'text-success';
      case 'debit':
        return 'text-info';
      case 'credit_addition':
        return 'text-primary';
      default:
        return 'text-base-content';
    }
  };

  if (transactionsLoading && transactions.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm border border-base-300">
        <div className="card-body">
          <h3 className="card-title text-lg mb-4">📊 Transaction History</h3>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-base-200 rounded-lg animate-pulse">
                <div className="space-y-1">
                  <div className="skeleton h-4 w-32"></div>
                  <div className="skeleton h-3 w-20"></div>
                </div>
                <div className="skeleton h-6 w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="card-title text-lg">📊 Transaction History</h3>
            <p className="text-sm text-base-content/60 mt-1">
              Total: {transactionsMeta.total} transactions
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <select 
              className="select select-bordered select-sm"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              aria-label="Filter by transaction type"
            >
              {transactionTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            <input
              type="date"
              className="input input-bordered input-sm"
              placeholder="From date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            
            <input
              type="date"
              className="input input-bordered input-sm"
              placeholder="To date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            
            <button
              className="btn btn-ghost btn-sm"
              onClick={clearFilters}
              disabled={transactionType === 'all' && !dateFrom && !dateTo}
            >
              Clear
            </button>
          </div>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📊</div>
            <p className="text-base-content/60">No transactions found</p>
            <p className="text-sm text-base-content/40 mt-1">
              {transactionType !== 'all' || dateFrom || dateTo
                ? 'Try adjusting your filters to see more results.'
                : 'Transaction history will appear here after you process resumes or purchase credits.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-3 bg-base-200 rounded-lg hover:bg-base-300 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="text-xl">
                      {getTransactionIcon(transaction.transaction_type)}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{transaction.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-base-content/60">
                          {formatDate(transaction.created_at)}
                        </span>
                        {transaction.resume_name && (
                          <span className="badge badge-outline badge-xs">
                            {transaction.resume_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`font-semibold ${getTransactionColor(transaction.transaction_type)}`}>
                      {transaction.transaction_type === 'debit' ? '-' : '+'}
                      {transaction.amount}
                    </div>
                    <div className="text-xs text-base-content/60">
                      Balance: {transaction.balance_after}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Load More Button */}
            {transactionsMeta.hasMore && (
              <div className="text-center mt-4">
                <button
                  className={`btn btn-outline btn-sm ${loadingMore ? 'loading' : ''}`}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
            
            {/* Footer Info */}
            <div className="text-center mt-4">
              <p className="text-xs text-base-content/60">
                Showing {transactions.length} of {transactionsMeta.total} transactions
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 